import React, { useEffect, useState } from "react";
import { Provider } from "react-redux";
import { Redirect, useHistory } from "react-router-dom";
import UpcomingMeetings from "./app/components/main/dashboard/upcoming-ongoing-events/upcoming-meetings/UpcomingMeetings";
import PortalItem from "./app/components/shared/portals/PortalItem";
import { store } from "./app/redux/store";
import Root from "./app/Root";
import TagManager from "react-gtm-module";
import { environment } from "./app/config/environments/environment";
import * as Sentry from "@sentry/react";
import { WebSocketService } from "./app/services/WebSocketService";
import Auth0TokenExchange from "./app/components/main/authentication/Auth0TokenExchange";
import { useAuth0 } from "@auth0/auth0-react";
import Cookies from 'js-cookie';
import useJwtToken from './app/config/auth/useJwtToken';
import { CallNotificationWidget } from "./app/components/shared/CallNotificationWidget";
import { initializeTokenValidation } from "./app/config/api-config/callApi";
import Loader from "./app/components/shared/loaders/Loader";
import SignIn from "./app/components/main/authentication/sign-in/SignIn";
import { useRefreshToken } from "./app/config/auth/useRefreshToken";

function App() {
  const channel = new BroadcastChannel('convay');
   const { isLoading, error } = useAuth0();
  const hasAuthToken = !!Cookies.get("auth_token");
  const [isTokenExchangeComplete, setIsTokenExchangeComplete] = useState(false);
  const history = useHistory();
  const authUser = useJwtToken();
  const [isAuthInitialized, setIsAuthInitialized] = useState(false);

const [isAuthenticated, setIsAuthenticated] = useState(false);
  useRefreshToken();

  // Initialize Sentry
  useEffect(() => {
    const sentryDsn = process.env.REACT_APP_SENTRY_DSN || environment.sentryDsn;
    if (sentryDsn) {
      Sentry.init({
        dsn: sentryDsn,
        environment: process.env.NODE_ENV || "development",
        tracesSampleRate: process.env.NODE_ENV === "production" ? 0.1 : 1.0,
        beforeSend(event) {
          // Filter out browser extension errors
          if (event.exception?.values?.[0]?.value?.includes("extension://")) {
            return null;
          }
          return event;
        },
      });
    }
  }, []);

useEffect(() => {
  const initializeAuth = async () => {
    try {
      const isValid = await initializeTokenValidation();
      setIsAuthenticated(isValid);

    } catch (error) {
      // Report authentication errors to Sentry
      Sentry.captureException(error, {
        tags: {
          component: "App",
          errorType: "authentication",
        },
      });
      setIsAuthenticated(false);
    } finally {
      setIsAuthInitialized(true);
    }
  };

  initializeAuth();
}, []);

  useEffect(() => {
    // Only connect when we have a valid user email
    if (!authUser?.user?.user_email) return;

    let wsService: any;

    try {
      const wsConfig = {
        url: process.env.REACT_APP_WS_URL ?? "http://localhost:8000/ws",
        userId: authUser?.user?.user_email,
        debug: true
      };

      wsService = WebSocketService.getInstance(wsConfig);

      // Avoid reconnecting while deactivating or already active
      if (!wsService.isActive && !wsService.isDeactivating) {
        wsService.connect();
        console.debug("WebSocket connected");
      } else {
        console.debug("Skipping connect: already active/deactivating");
      }
    } catch (err) {
      console.error("WebSocket connection error:", err);
      // Report WebSocket errors to Sentry
      Sentry.captureException(err, {
        tags: {
          component: "App",
          errorType: "websocket",
        },
        extra: {
          userId: authUser?.user?.user_email,
        },
      });
    }

    // Cleanup: disconnect when component unmounts or authUser changes
    return () => {
      try {
        if (wsService) {
          wsService.disconnect();
          console.debug("WebSocket disconnected");
        }
      } catch (err) {
        console.error("WebSocket disconnect error:", err);
        // Report WebSocket disconnect errors to Sentry
        Sentry.captureException(err, {
          tags: {
            component: "App",
            errorType: "websocket_disconnect",
          },
        });
      }
    };
  }, [authUser]);

  // Set user context for Sentry when authenticated
  useEffect(() => {
    if (authUser?.user) {
      Sentry.setUser({
        id: authUser?.user?.ID?.toString(),
        email: authUser?.user?.user_email,
        username: authUser?.user?.user_name,
      });
    } else {
      Sentry.setUser(null);
    }
  }, [authUser]);

  useEffect(() => {
    channel.onmessage = (event) => {
      if(event.data.includes('logout')) {
        // Clear Sentry user context on logout
        Sentry.setUser(null);
        window.location.reload();
      }
    };

    TagManager.initialize({
      gtmId: environment.gtmId,
    });
  }, []);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Loader circleStyle={{ background: "#ECEDF0" }} />
      </div>
    );
  }

    if (!isAuthInitialized) {
    return (
      <Provider store={store}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <Loader circleStyle={{ background: "#ECEDF0" }} />
        </div>
      </Provider>
    );
  }

  return (
    <>
      <Auth0TokenExchange onTokenExchangeComplete={() => {
        setIsTokenExchangeComplete(true);
      }} />
    <Provider store={store}>
      <CallNotificationWidget />
      <Root />
    </Provider>
    </>
  );
}

export default Sentry.withProfiler(App);
